// The /threads composer — bottom-anchored, multi-line, auto-growing.
//
// It is NEVER position:fixed. The routed scene is an animated <motion.div>
// (Main.jsx §70-78) whose transform becomes the containing block for `fixed`,
// so a fixed composer would pin itself to the animation rather than to the
// viewport. It is the last flex child of the page's `h-full min-h-0 flex-col`
// column instead, which is what actually anchors it.
//
// The lens picker moved to the page's top bar (SIDEBAR_LENS_REWORK —
// LensScopePicker via ThreadsTopBar): the scope still rides each ask exactly
// as before (MainThreads owns it; the server stores it as the thread's
// `last_scope`), the bar is just where the control lives now. The web toggle
// stays here — like the question text, it is a property of the next send.
import { useCallback, useEffect, useRef, useState } from "react";
import { CornerDownLeft, Globe, Square } from "lucide-react";

// The server's cap (routes/v2/chat/context.py MAX_QUESTION_CHARS). Typing past
// it is refused here rather than truncated there.
const MAX_QUESTION_CHARS = 2000;
// The counter is noise until it is nearly information.
const COUNTER_FROM = MAX_QUESTION_CHARS - 200;
// ~8 rows at this type size; past it the textarea scrolls instead of growing.
const MAX_COMPOSER_PX = 176;

// The web toggle — kept in the composer because, like the scope, it is a
// property of the next question: ON means the server runs a web search for
// the message and answers from corpus + web together (guaranteed server-side —
// not a hint to the model). Rendered ALWAYS: the UI cannot know whether the
// deploy has web search configured; a server without it refuses the forced ask
// with a clear error the page already surfaces inline (accepted v1 — birr's
// deploys all have it). Sticky semantics live in MainThreads, not here.
const WebToggle = ({ active, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => onChange(!active)}
    disabled={disabled}
    aria-pressed={active}
    title={
      active
        ? "Web search is ON for this conversation — every question also searches the web until you turn it off"
        : "Search the web for this message"
    }
    className={`inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
      active
        ? "border-velBlue/60 bg-velBlue/15 text-velBlue ring-1 ring-velBlue/30 hover:bg-velBlue/25"
        : "border-mainDivider bg-mainButtonBg/35 text-mutedText hover:bg-mainButtonBg/60 hover:text-velBlue"
    }`}
  >
    <Globe size={12} className="shrink-0" />
    <span>Web</span>
  </button>
);

const Composer = ({
  busy,
  onAsk,
  onStop,
  webSearch, // the web toggle state (owned by MainThreads, like scope)
  onWebSearchChange,
  inputRef, // optional: the page's handle on the textarea (seed clicks focus it)
  scopePicker, // DD only: the scope picker node (GS renders it in the top bar)
}) => {
  const [value, setValue] = useState("");
  const localRef = useRef(null);
  const taRef = inputRef || localRef;

  const autosize = useCallback(
    (el) => {
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, MAX_COMPOSER_PX)}px`;
    },
    [],
  );
  useEffect(() => {
    autosize(taRef.current);
  }, [value, autosize, taRef]);

  const submit = useCallback(() => {
    const q = value.trim();
    if (!q || busy) return;
    setValue("");
    onAsk(q);
  }, [value, busy, onAsk]);

  const canSend = value.trim().length > 0 && !busy;

  return (
    <div className="rounded-2xl border border-mainDivider bg-mainButtonBg/25 px-3 py-2.5 transition-colors focus-within:border-velBlue/40">
      <textarea
        ref={taRef}
        rows={1}
        value={value}
        maxLength={MAX_QUESTION_CHARS}
        onChange={(e) => setValue(e.target.value)}
        onInput={(e) => autosize(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
            return;
          }
          if (e.key === "Escape") taRef.current?.blur();
        }}
        placeholder={busy ? "Answering…" : "Ask anything about the corpus…"}
        className="block w-full resize-none border-0 bg-transparent px-1 text-[13px] leading-relaxed text-mainText outline-none placeholder:text-mutedText/60"
        style={{ maxHeight: MAX_COMPOSER_PX }}
      />

      <div className="mt-2 flex items-center gap-2">
        {scopePicker}
        <WebToggle
          active={webSearch === true}
          onChange={onWebSearchChange}
          disabled={busy}
        />
        <div className="flex-1" />
        {value.length >= COUNTER_FROM && (
          <span
            className={`tabular-nums text-[10px] ${
              value.length >= MAX_QUESTION_CHARS ? "text-amber-400" : "text-mutedText"
            }`}
          >
            {value.length}/{MAX_QUESTION_CHARS}
          </span>
        )}
        {busy ? (
          <button
            type="button"
            onClick={onStop}
            title="Stop generating — the partial answer is kept"
            className="rounded-lg border border-rose-400/50 p-1.5 text-rose-400 transition hover:bg-rose-400/10"
          >
            <Square size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            title="Ask (Enter) · Shift+Enter for a new line"
            className="rounded-lg border border-mainDivider p-1.5 text-mutedText transition hover:border-velBlue/50 hover:text-velBlue disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-mainDivider disabled:hover:text-mutedText"
          >
            <CornerDownLeft size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Composer;
